import type { LocalizedSpeechPreviewRequest } from '@/shared/api/types/localized-speech-types'
import type { TranslationJobScope } from '@/shared/constants/status'
import { useMemo } from 'react'
import {
  buildTranslationJobEntityKey,
  useTranslationJobsStore,
} from '@/entities/localized-speech/store/translation-jobs-store'
import { TRANSLATION_JOB_STATUS } from '@/shared/constants/status'

interface UseLocalizedTranslationJobControllerParams {
  /** Scope dùng để lọc job dịch đúng loại entity hiện tại. */
  scope: TranslationJobScope
  /** Id ổn định dùng để ghép job nền về đúng form hiện tại. */
  entityId: string
  /** Nhãn tùy chọn hiển thị trong toast hoặc background jobs panel. */
  entityLabel?: string
  /** Đường dẫn tùy chọn để mở lại entity từ background jobs panel. */
  entityPath?: string
  /** Bật/tắt tạo audio khi enqueue job dịch. */
  audioEnabled: boolean
  /** Field dùng để tạo speech/audio. Mặc định giữ description để tương thích flow cũ. */
  speechField?: string
}

interface EnqueueTranslationJobInput {
  /** Các field nguồn đã trim dùng làm input cho API dịch. */
  fields: Record<string, string>
  /** Ngôn ngữ nguồn, thường là active language đã bỏ region suffix. */
  inputLanguage: string
  /** Danh sách ngôn ngữ đích cần dịch/tạo audio. */
  outputLanguages: string[]
  sourceTranslations?: unknown
}

/**
 * Hook bọc quyền truy cập translation jobs store cho workflow localized translation.
 *
 * Component UI không cần biết cách store lọc job running/success hay cách build payload;
 * hook này gom phần đó lại để sau này đổi store/API chỉ phải sửa một nơi.
 */
export function useLocalizedTranslationJobController({
  scope,
  entityId,
  entityLabel,
  entityPath,
  audioEnabled,
  speechField,
}: UseLocalizedTranslationJobControllerParams) {
  const startJob = useTranslationJobsStore((state) => state.startJob)
  const consumeJob = useTranslationJobsStore((state) => state.consumeJob)
  const consumeJobRestore = useTranslationJobsStore((state) => state.consumeJobRestore)
  const dismissJob = useTranslationJobsStore((state) => state.dismissJob)
  const clearEntityJobs = useTranslationJobsStore((state) => state.clearEntityJobs)
  const jobs = useTranslationJobsStore((state) => state.jobs)
  const drafts = useTranslationJobsStore((state) => state.drafts)
  const setDraft = useTranslationJobsStore((state) => state.setDraft)
  const clearDraft = useTranslationJobsStore((state) => state.clearDraft)

  const draftKey = useMemo(() => buildTranslationJobEntityKey(scope, entityId), [scope, entityId])

  const runningJobs = useMemo(
    () =>
      jobs.filter(
        (job) =>
          job.scope === scope &&
          job.entityId === entityId &&
          job.status === TRANSLATION_JOB_STATUS.PROCESSING,
      ),
    [entityId, jobs, scope],
  )
  const pendingSuccessJobs = useMemo(
    () =>
      jobs.filter(
        (job) =>
          job.scope === scope &&
          job.entityId === entityId &&
          job.status === TRANSLATION_JOB_STATUS.SUCCESS &&
          !job.consumed,
      ),
    [entityId, jobs, scope],
  )

  /**
   * Tạo payload chuẩn và enqueue job dịch nền cho entity hiện tại.
   */
  function enqueueTranslationJob(input: EnqueueTranslationJobInput) {
    const payload: LocalizedSpeechPreviewRequest = {
      fields: input.fields,
      inputLanguage: input.inputLanguage,
      outputLanguages: input.outputLanguages,
      audioEnabled,
      speechField: audioEnabled ? (speechField ?? 'description') : '',
    }

    return startJob({
      scope,
      entityId,
      ...(entityLabel ? { entityLabel } : {}),
      ...(entityPath ? { entityPath } : {}),
      payload,
      ...(input.sourceTranslations !== undefined
        ? { sourceTranslations: input.sourceTranslations }
        : {}),
    })
  }

  return {
    drafts,
    setDraft,
    clearDraft,
    runningJobs,
    pendingSuccessJobs,
    enqueueTranslationJob,
    consumeJob,
    consumeJobRestore,
    dismissJob,
    clearEntityJobs,
    draftKey,
  }
}
